'use client';

import { useCallback, useState } from 'react';
import { useLocale } from 'next-intl';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { purchaseService } from '@/features/payment/api/purchase';
import { projectsService } from '@/features/project/api/projects';
import {
  isPaddleEnabled,
  openPackCheckout,
  PADDLE_PACK_PRICE_ID,
  toPaddleLocale,
} from '@/shared/lib/paddle';
import { useUser } from '@/features/auth/model/auth.selectors';
import { readGeoCountry } from '@/shared/lib/geo-country';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import {
  AnalyticsEvent,
  trackEvent,
} from '@/shared/lib/analytics';

// Backoff schedule (ms) for the post-payment grant poll. Paddle's
// `checkout.completed` fires the instant payment is confirmed in the browser,
// but the Kit grant lands asynchronously via the webhook a beat later. We poll
// the building a few times (~10s total) so the UI flips the moment the grant
// commits, instead of waiting on the WS `user/updated` event or a manual
// refresh. Short, front-loaded delays catch the common fast case quickly.
const PACK_POLL_DELAYS_MS = [700, 1000, 1500, 2500, 4000];

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * After checkout completes, poll the building until its Kit grant lands, then
 * invalidate so mounted UI reflects it. Fire-and-forget; uses the stable
 * QueryClient (not React state), so it's safe past unmount. This only
 * ACCELERATES the flip — the WS `user/updated pack_purchase` event and the
 * reconcile layers (L1/L2/L3) remain the authoritative backstop if the grant
 * takes longer than this window or the poll errors out.
 */
async function pollUntilPackGranted(
  queryClient: QueryClient,
  projectId: string
): Promise<void> {
  for (const delay of PACK_POLL_DELAYS_MS) {
    await sleep(delay);

    let isActive = false;
    try {
      const res = await projectsService.get(projectId);
      isActive = res.data.attributes.pack?.is_active === true;
    } catch {
      // Transient read failure — keep polling; the WS/reconcile backstop covers
      // a total miss. Never surface this to the buyer.
      continue;
    }

    if (isActive) {
      // Grant landed — refresh the building list + render gate so the Kit tag
      // flips and Generate unlocks. Mirrors the WS pack_purchase reconcile.
      queryClient.invalidateQueries({ queryKey: cacheKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: cacheKeys.queueStatus.all });
      return;
    }
  }
}

/**
 * Web Facade Kit checkout: create the correlation intent, then open the Paddle
 * overlay bound to the building. The API webhook owns the grant; the
 * `checkout.completed` event only refetches /projects locally so the building's
 * `pack.is_active` flips without waiting on the webhook round-trip (the WS
 * `user/updated` `pack_purchase` reconcile covers other tabs/devices). No
 * client-side payment-state interpretation (API-driven state rule).
 */
/**
 * Host callbacks the paywall wires in to keep its overlay clickable and show
 * the post-purchase success state in place (parity with Apple/Android, which
 * stay open on the celebration):
 *  - `onPurchased` — checkout.completed: flip the paywall body to its success
 *    celebration instead of closing it.
 *  - `onClosed` — checkout.closed: the overlay went away (purchase OR cancel),
 *    so the host can restore its modal lock.
 */
export interface PackCheckoutCallbacks {
  onPurchased?: () => void;
  onClosed?: () => void;
}

export function usePackCheckout(
  projectId: string | undefined,
  callbacks?: PackCheckoutCallbacks
) {
  const queryClient = useQueryClient();
  const user = useUser();
  const locale = useLocale();
  const [isLaunching, setIsLaunching] = useState(false);
  const onPurchased = callbacks?.onPurchased;
  const onClosed = callbacks?.onClosed;

  const available = isPaddleEnabled() && !!projectId;

  const startCheckout = useCallback(async () => {
    if (!projectId || !PADDLE_PACK_PRICE_ID) {
      return;
    }

    setIsLaunching(true);
    try {
      trackEvent(AnalyticsEvent.PACK_PURCHASE_STARTED, { project_bound: true });

      const intent = await purchaseService.createPackIntent(projectId);

      await openPackCheckout({
        priceId: PADDLE_PACK_PRICE_ID,
        correlationToken: intent.correlation_token,
        // Prefill buyer email (always known — logged-in) + live geo country so
        // Paddle skips its "Your details" step. Country comes from the geo
        // header (not the stored profile) so it tracks the buyer's actual
        // location; Paddle still owns final tax based on payment + location.
        customerEmail: user?.email || undefined,
        countryCode: readGeoCountry(),
        // Match the checkout UI language to the app's active locale (Paddle
        // otherwise defaults to browser language / English regardless of UI).
        locale: toPaddleLocale(locale),
        onCompleted: () => {
          // Optimistic immediate refresh — covers the fast path where the grant
          // already committed by the time the JS event fires.
          queryClient.invalidateQueries({ queryKey: cacheKeys.projects.all });
          queryClient.invalidateQueries({
            queryKey: cacheKeys.queueStatus.all,
          });
          // Then poll the building until the async webhook grant lands, so the
          // Kit tag flips in-moment without a manual refresh. WS
          // `user/updated pack_purchase` + reconcile remain the backstop.
          void pollUntilPackGranted(queryClient, projectId);
          // Let the host paywall flip to its success celebration in place.
          onPurchased?.();
        },
        onClosed,
      });
    } finally {
      setIsLaunching(false);
    }
  }, [projectId, queryClient, user?.email, locale, onPurchased, onClosed]);

  return { available, isLaunching, startCheckout };
}
